import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getAccount } from '@app/reducers/account/account-service.selectors';
import { isAuthenticated } from '@app/reducers/auth/auth-service.selectors';
import { getAuthenticatedUser } from '@app/reducers/orm/employee/employee.selectors';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import * as Sentry from '@sentry/angular';
import { combineLatest, skip, Subject, throttleTime } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Only propagate `sentry-trace`/`baggage` headers to our own same-origin `/api`
 * backend so frontend spans link to backend spans. Cross-origin third parties
 * (analytics, maps, etc.) are intentionally excluded.
 */
export const TRACE_PROPAGATION_TARGETS: (string | RegExp)[] = [/^\/api\//];

const IDENTIFYING_HEADERS = ['forwarded', '-ip', 'remote-', 'via', '-user'];

/**
 * Leaving `dataCollection` unset collects user info, cookies and full request and
 * response bodies, so this keeps collection to the restrictive baseline explicitly.
 */
export const DATA_COLLECTION: Sentry.BrowserOptions['dataCollection'] = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: IDENTIFYING_HEADERS },
    response: { deny: IDENTIFYING_HEADERS },
  },
  httpBodies: [],
  urlQueryParams: { deny: IDENTIFYING_HEADERS },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
};

/**
 * A tab left open across a release imports a chunk the new build no longer serves.
 * Every engine words the failed import differently, and the bundler's own chunk loader
 * adds a wording of its own, so suppressing the one behaviour takes all four.
 */
const STALE_CHUNK_ERRORS = [
  'Loading chunk', // bundler chunk loader, any browser
  'Failed to fetch dynamically imported module', // Chrome
  'Importing a module script failed.', // Safari
  'error loading dynamically imported module', // Firefox
];

/**
 * Analytics and advertising hosts, reached only by tags Google Tag Manager injects.
 * An ad blocker rejects those requests in the client, which surfaces as an unhandled
 * `TypeError` carrying nothing about our own code. Subdomains are covered too.
 */
const AD_AND_ANALYTICS_DOMAINS = [
  'ads.linkedin.com',
  'cello.so',
  'doubleclick.net',
  'google-analytics.com',
  'google.com',
  'googleadservices.com',
  'googletagmanager.com',
  'hubspot.com',
];

const AD_AND_ANALYTICS_HOST_PATTERN = AD_AND_ANALYTICS_DOMAINS.map((domain) => domain.replace(/\./g, '\\.')).join('|');

/**
 * Sentry's fetch instrumentation appends the request host to the message, and that is
 * the only place it survives — the event itself carries no request URL. Requiring the
 * host keeps a bare `Failed to fetch`, which can come from our own traffic, reportable.
 */
const BLOCKED_THIRD_PARTY_FETCH = new RegExp(
  `^(?:TypeError: )?Failed to fetch \\((?:[\\w-]+\\.)*(?:${AD_AND_ANALYTICS_HOST_PATTERN})\\)$`,
);

export const IGNORED_ERRORS: (string | RegExp)[] = [
  /^Error: Could not load Refiner client.$/,
  ...STALE_CHUNK_ERRORS,
  BLOCKED_THIRD_PARTY_FETCH,
];

/**
 * zone.js patches every global listener, so its frames wrap an error thrown by an injected
 * tag exactly as they wrap one of our own. Treating the bundle that carries it as our code
 * would therefore vouch for every stack, which is why it is excluded here. The app builder
 * emits it unhashed while serving and hashed in a production build.
 */
const POLYFILLS_BUNDLE = /\/polyfills(-[^/]+)?\.js$/;

/**
 * Our own scripts are the only ones served from the document's origin; a tag Google Tag
 * Manager injects loads its code from the vendor's host. Requiring a `.js` file on top of
 * that rules out a frame compiled at runtime against the document URL — `eval`,
 * `new Function`, an inline `<script>` — which reports itself under the current route path
 * and so carries our origin without being ours.
 */
const isOwnCodeFrame = (filename: string | undefined): boolean => {
  if (filename === undefined || !filename.startsWith(`${location.origin}/`)) {
    return false;
  }

  const path = filename.split(/[?#]/)[0];

  return path.endsWith('.js') && !POLYFILLS_BUNDLE.test(path);
};

/**
 * An error raised entirely inside code we never built — an analytics or session-recording
 * tag, a browser extension — reaches us only because zone.js forwards it. No sourcemap of
 * ours can resolve those frames and the report names nothing we could fix, so it is dropped
 * rather than grouped. This cannot move to `ignoreErrors`, which matches on the message
 * alone: keying on the stack is what keeps the same message reportable when it does come
 * from our own code. An event without frames says nothing either way and is kept.
 */
export const isThirdPartyScriptError = (event: Sentry.ErrorEvent): boolean => {
  const frames = event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];

  return frames.length > 0 && !frames.some((frame) => isOwnCodeFrame(frame.filename));
};

/** Extract a string message from an arbitrary thrown value (Error, HttpErrorResponse, string, ...). */
const getErrorMessage = (exception: unknown): string | undefined => {
  if (typeof exception === 'string') {
    return exception;
  }

  if (typeof exception === 'object' && exception !== null && 'message' in exception) {
    return typeof exception.message === 'string' ? exception.message : undefined;
  }

  return undefined;
};

@Injectable({
  providedIn: 'root',
})
export class SentryLoggingService {
  #errorListener$ = new Subject<unknown>();
  #store = inject(Store<AppState>);
  #featureService = inject(FeatureService);
  #destroyRef = inject(DestroyRef);

  isInitialized = false;

  startLogging() {
    if (!this.#sentryLoggingEnabled()) return;

    this.#init();
  }

  #init() {
    if (this.isInitialized) return;

    if (!environment.sentryDsn) {
      throw new Error('Sentry DSN is not set');
    }

    if (!environment.env) {
      throw new Error('Environment is not set');
    }

    const { tracesSampleRate, profileSessionSampleRate } =
      this.#featureService.getAllFeatureVariables(featureFlag.SENTRY_LOGGING) || {};

    const getNumberValueFor = (value: unknown) => (typeof value === 'number' ? value : 0.0);

    Sentry.init({
      dsn: environment.sentryDsn,
      environment: environment.env,
      release: environment.version,
      integrations: [Sentry.browserTracingIntegration(), Sentry.browserProfilingIntegration()],
      tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
      dataCollection: DATA_COLLECTION,
      tracesSampleRate: getNumberValueFor(tracesSampleRate),
      profileSessionSampleRate: getNumberValueFor(profileSessionSampleRate),
      profileLifecycle: 'trace',
      ignoreErrors: IGNORED_ERRORS,
      beforeSend: (event: Sentry.ErrorEvent, hint: Sentry.EventHint) => {
        const originalException = hint.originalException;

        if (typeof originalException === 'undefined') {
          return null;
        }

        const errorMessage = getErrorMessage(originalException);
        const nonErrorExceptionEvent = event?.message?.includes('Non-Error exception captured');
        const nonErrorException = errorMessage?.includes('Non-Error exception captured');
        const httpFailureResponse = errorMessage?.includes('Http failure response');

        if (nonErrorExceptionEvent || nonErrorException || httpFailureResponse) {
          return null;
        }

        if (originalException instanceof HttpErrorResponse && originalException.status === 422) {
          return null;
        }

        if (isThirdPartyScriptError(event)) {
          return null;
        }

        return event;
      },
    });

    this.#trackIdentity();

    this.#errorListener$.pipe(throttleTime(5000), takeUntilDestroyed(this.#destroyRef)).subscribe((exception) => {
      Sentry.captureException(exception);
    });

    this.isInitialized = true;
  }

  handleError(exception: unknown): void {
    if (!this.#sentryLoggingEnabled()) return;

    this.#errorListener$.next(exception);
  }

  #trackIdentity() {
    combineLatest([
      this.#store.select(isAuthenticated).pipe(skip(1)),
      this.#store.select(getAuthenticatedUser).pipe(skip(1)),
      this.#store.select(getAccount).pipe(skip(1)),
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      /**
       * These 3 values come from different endpoints,
       * login is setting authenticated=true
       * but then employees brings the user data and
       * subscription the account data.
       * So it might happen that when the data is streamed into this subsription,
       * not everything is there (yet) and it looks like it's authenticated
       * without user or account info.
       * Something similar (but in the opposite direction)
       * happens during logout.
       */
      .subscribe(([authenticated, user, account]) => {
        Sentry.setExtra('authenticated', authenticated);

        if (!authenticated) {
          Sentry.setUser(null);
          return;
        }

        if (user) {
          Sentry.setUser({
            id: user.id,
            username: user.name,
            email: user.email,
          });
        }

        if (account) {
          Sentry.setExtra('Account', {
            id: account.id,
            name: account.company,
          });
        }
      });
  }

  #sentryLoggingEnabled(): boolean {
    return this.#featureService.isEnabled(featureFlag.SENTRY_LOGGING);
  }
}
